import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { employeeMessageActions } from '@nxsys/store/slices/employeeMessage/reducer/employeeMessageSlice';
import { CreateConversationParams } from '@nxsys/store/slices/message/types';
import { showMessage } from '@nxsys/utils/message';
import { Form, message } from 'antd';
import { FormProps } from 'antd/lib';
import TextArea from 'antd/lib/input/TextArea';
import { isArray } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

const priorityOptions = [
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
];

const statusOptions = [
  { label: 'New', value: 'NEW' },
  { label: 'WIP', value: 'WIP' },
  { label: 'Closed', value: 'CLOSED' },
];

type CreateNewMessageModalProps = {
  isOpen: boolean;
  toggle: () => void;
  employeeUserId: string;
  employeeName: string;
  onSuccess?: () => void;
};

const CreateNewMessageModal: React.FC<CreateNewMessageModalProps> = ({
  isOpen,
  toggle,
  employeeUserId,
  employeeName,
  onSuccess,
}) => {
  const { messages: intlMessages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFinish: FormProps['onFinish'] = async (values) => {
    try {
      setIsSubmitting(true);

      const params: CreateConversationParams = {
        subject: values.subject,
        description: values.content || '',
        priority:
          typeof values.priority === 'object'
            ? values.priority?.value
            : values.priority,
        status:
          typeof values.status === 'object'
            ? values.status?.value
            : values.status,
        dueDate: values.dueDate
          ? moment(values.dueDate).toISOString()
          : undefined,
        userIds: [employeeUserId],
      };

      await dispatch(
        employeeMessageActions.eeCreateConversation(params),
      ).unwrap();

      showMessage({
        type: 'success',
        content: intlMessages['common.addSuccess'],
      });
      form.resetFields();
      toggle();
      onSuccess?.();
    } catch (err: any) {
      let msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create conversation';
      if (isArray(msg)) msg = msg.join(', ');
      message.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={intlMessages['common.addNewMessage']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: intlMessages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: intlMessages['common.send'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          status: statusOptions[0],
        }}
      >
        <Form.Item label={`To`}>
          <AppInput value={employeeName} disabled />
        </Form.Item>
        <Form.Item
          label={intlMessages['common.subject']}
          name="subject"
          rules={[
            { required: true, message: intlMessages['form.pleaseEnter'] },
          ]}
        >
          <AppInput placeholder={intlMessages['form.enter']} />
        </Form.Item>
        <Form.Item
          label={intlMessages['common.status']}
          name="status"
          rules={[
            { required: true, message: intlMessages['form.pleaseSelect'] },
          ]}
        >
          <AppSelect
            options={statusOptions}
            placeholder={intlMessages['form.select']}
          />
        </Form.Item>
        <Form.Item label={intlMessages['common.priority']} name="priority">
          <AppSelect
            options={priorityOptions}
            placeholder={intlMessages['form.select']}
          />
        </Form.Item>
        <Form.Item label={intlMessages['common.dueDate']} name="dueDate">
          <AppDatePicker
            format="DD/MM/YYYY"
            placeholder={intlMessages['form.selectDate']}
          />
        </Form.Item>
        <Form.Item
          label={intlMessages['common.typeMessage']}
          name="content"
          rules={[
            { required: true, message: intlMessages['form.pleaseEnter'] },
          ]}
        >
          <TextArea placeholder={intlMessages['form.enter']} rows={4} />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default CreateNewMessageModal;
